import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useDropzone, FileRejection } from "react-dropzone";
import { IPerfil } from "../types/perfil.types";
import { uploadIcone, uploadFigura, uploadDesenhoTecnico, definirNomeMidiaPerfil } from "../services/perfilService";
import { useNavigation } from "contexts/notificationsContext";
import { CampoNomeArquivo } from "../../components/CampoNomeArquivo";
import { useMidiaCompartilhada } from "../../hooks/useMidiaCompartilhada";
import { nomeArquivoPadrao } from "../../hooks/useNomeArquivoPadrao";

interface ModalGerenciamentoMidiasProps {
  open: boolean;
  onClose: () => void;
  perfil: Partial<IPerfil>;
  onSave: (midias: {
    iconeUrl?: string;
    iconeNomeArquivo?: string;
    figuraUrl?: string;
    figuraNomeArquivo?: string;
    desenhoTecnicoUrl?: string;
    desenhoTecnicoNomeArquivo?: string;
  }, idAlvo: number) => void;
}

// FASE 05 — 2ª validação/PO 2026-07-24 (F2): a aba "Desenho Técnico" não existe para
// Perfis; fica oculta por esta constante (basta trocar para true para reativar, os
// endpoints de upload/remoção continuam existindo no backend).
const EXIBIR_ABA_DESENHO_TECNICO = false;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`midia-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface UploadAreaProps {
  accept: string;
  arquivoAtual?: string;
  nomeArquivo?: string;
  onFileSelect: (file: File) => void;
  onFileRejected?: (motivo: string) => void;
}

const MIME_POR_EXTENSAO: Record<string, string[]> = {
  ".bmp": ["image/bmp", "image/x-ms-bmp"],
  ".png": ["image/png"],
  ".jpg": ["image/jpeg"],
  ".jpeg": ["image/jpeg"],
  ".pdf": ["application/pdf"],
};

// FASE 05 — 4ª validação/D8 2026-07-30: aceitar por MIME **e** extensão (o mapa anterior,
// MIME com lista de extensões vazia, rejeitava em silêncio arquivos com MIME fora do
// esperado — sem preview, sem erro e sem request). Rejeição agora é notificada.
const montarAccept = (accept: string): Record<string, string[]> => {
  const resultado: Record<string, string[]> = {};
  accept.split(",").forEach((extBruta) => {
    const ext = extBruta.trim().toLowerCase();
    (MIME_POR_EXTENSAO[ext] ?? []).forEach((mime) => {
      if (!resultado[mime]) resultado[mime] = [];
      if (!resultado[mime].includes(ext)) resultado[mime].push(ext);
    });
  });
  return resultado;
};

const UploadArea: React.FC<UploadAreaProps> = ({
  accept,
  arquivoAtual,
  nomeArquivo,
  onFileSelect,
  onFileRejected,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
        return;
      }
      if (fileRejections.length > 0) {
        onFileRejected?.(
          `Arquivo não aceito. Extensões permitidas: ${accept.toUpperCase()}`
        );
      }
    },
    [onFileSelect, onFileRejected, accept]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: montarAccept(accept),
    multiple: false,
  });

  return (
    <Box>
      {/* Área de Upload */}
      <Paper
        {...getRootProps()}
        sx={{
          p: 4,
          textAlign: "center",
          border: isDragActive ? "2px dashed #3C90AD" : "2px dashed #ccc",
          backgroundColor: isDragActive ? "#f0f8ff" : "#fafafa",
          cursor: "pointer",
          minHeight: 300,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          "&:hover": {
            borderColor: "#3C90AD",
            backgroundColor: "#f8f8f8",
          },
        }}
      >
        <input {...getInputProps()} />
        
        {arquivoAtual ? (
          <Box>
            {arquivoAtual.endsWith(".pdf") ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="primary">
                  📄 PDF Carregado
                </Typography>
              </Box>
            ) : (
              <img
                src={arquivoAtual}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: 200,
                  objectFit: "contain",
                  marginBottom: 16,
                }}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {nomeArquivo || "Arquivo carregado"}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Arraste outro arquivo ou clique para substituir
            </Typography>
          </Box>
        ) : (
          <Box>
            <CloudUploadIcon sx={{ fontSize: 48, color: "#999", mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Arraste e solte a imagem aqui dentro ou
            </Typography>
            <Typography variant="body1" color="text.secondary">
              clique aqui para abrir a janela de seleção
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const ModalGerenciamentoMidias: React.FC<ModalGerenciamentoMidiasProps> = ({
  open,
  onClose,
  perfil,
  onSave,
}) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const { openNotification } = useNavigation();
  
  // Estados temporários para os arquivos selecionados
  const [icone, setIcone] = useState<{ url?: string; nome?: string; file?: File }>({
    url: perfil.iconeUrl,
    nome: perfil.iconeNomeArquivo,
  });
  const [figura, setFigura] = useState<{ url?: string; nome?: string; file?: File }>({
    url: perfil.figuraUrl,
    nome: perfil.figuraNomeArquivo,
  });
  const [desenhoTecnico, setDesenhoTecnico] = useState<{ url?: string; nome?: string; file?: File }>({
    url: perfil.desenhoTecnicoUrl,
    nome: perfil.desenhoTecnicoNomeArquivo,
  });

  // Reporte de 31/08 (item 6): nomes com que o modal ABRIU — a comparacao de "nome alterado"
  // e contra eles, nao contra o cadastro (a Etapa C ja sugere <codigo>.PNG em produto novo).
  const nomesIniciais = React.useRef<{ icone?: string; figura?: string; desenhoTecnico?: string }>({});

  // Atualizar estados quando perfil mudar
  React.useEffect(() => {
    if (open) {
      // Etapa C: em cadastro NOVO sem nome informado, sugere <código>.BMP/.PNG/.PDF.
      const ehNovo = !perfil.dataCriacao;
      setIcone({
        url: perfil.iconeUrl,
        nome: nomeArquivoPadrao(perfil.codigo, ".BMP", ehNovo, perfil.iconeNomeArquivo),
      });
      setFigura({
        url: perfil.figuraUrl,
        nome: nomeArquivoPadrao(perfil.codigo, ".PNG", ehNovo, perfil.figuraNomeArquivo),
      });
      setDesenhoTecnico({
        url: perfil.desenhoTecnicoUrl,
        nome: nomeArquivoPadrao(perfil.codigo, ".PDF", ehNovo, perfil.desenhoTecnicoNomeArquivo),
      });
      nomesIniciais.current = {
        icone: nomeArquivoPadrao(perfil.codigo, ".BMP", ehNovo, perfil.iconeNomeArquivo),
        figura: nomeArquivoPadrao(perfil.codigo, ".PNG", ehNovo, perfil.figuraNomeArquivo),
        desenhoTecnico: nomeArquivoPadrao(perfil.codigo, ".PDF", ehNovo, perfil.desenhoTecnicoNomeArquivo),
      };
    }
  }, [open, perfil]);

  const handleFileSelect = (tipo: "icone" | "figura" | "desenhoTecnico", file: File) => {
    const url = URL.createObjectURL(file);
    
    switch (tipo) {
      case "icone":
        setIcone({ url, nome: file.name, file });
        break;
      case "figura":
        setFigura({ url, nome: file.name, file });
        break;
      case "desenhoTecnico":
        setDesenhoTecnico({ url, nome: file.name, file });
        break;
    }
  };

  // Ajustes 25/08 (item 3): há algo novo para gravar nas três abas?
  const temArquivoNovo = !!(icone.file || figura.file || desenhoTecnico.file);
  // Reporte de 31/08 (item 6): informar só o NOME também é alteração gravável.
  const temNomeAlterado =
    icone.nome !== nomesIniciais.current.icone ||
    figura.nome !== nomesIniciais.current.figura ||
    desenhoTecnico.nome !== nomesIniciais.current.desenhoTecnico;

  // Etapa D: quantos OUTROS produtos usam cada um destes nomes.
  const compartilhandoIcone = useMidiaCompartilhada(open, "perfis", 1, icone.nome, perfil.idPerfil);
  const compartilhandoFigura = useMidiaCompartilhada(open, "perfis", 2, figura.nome, perfil.idPerfil);
  const compartilhandoDesenho = useMidiaCompartilhada(open, "perfis", 3, desenhoTecnico.nome, perfil.idPerfil);

  const handleSalvar = async () => {
    // Verificar se o perfil já foi salvo (tem ID)
    if (!perfil.idPerfil) {
      openNotification({ 
        message: "Salve o perfil primeiro antes de fazer upload de mídias", 
        type: "warning" 
      });
      return;
    }

    // Etapa D (F05A-P3b): arquivo compartilhado altera a mídia de TODOS os produtos que o usam.
    const compartilhados = [
      icone.file ? compartilhandoIcone : 0,
      figura.file ? compartilhandoFigura : 0,
      desenhoTecnico.file ? compartilhandoDesenho : 0,
    ].reduce((a, b) => a + b, 0);
    if (compartilhados > 0) {
      const segue = window.confirm(
        `Algum dos arquivos é usado por outros produtos ( no total). ` +
        `Substituí-lo altera a mídia de todos eles. Deseja continuar?`
      );
      if (!segue) return;
    }

    setUploading(true);
    try {
      // Plano 21/09/2026 (Frente 3): cada gravação devolve o id do registro alterado — com o
      // perfil Disponível, a API grava na cópia de trabalho. As chamadas seguintes já vão para
      // esse id, e a página navega para ele ao final.
      let idAlvo = perfil.idPerfil!;
      const midiasAtualizadas: {
        iconeUrl?: string;
        iconeNomeArquivo?: string;
        figuraUrl?: string;
        figuraNomeArquivo?: string;
        desenhoTecnicoUrl?: string;
        desenhoTecnicoNomeArquivo?: string;
      } = {};

      // FASE 05 — 3ª validação/PO 2026-07-28 (Frente 3): upload que falha vira ERRO visível.
      // Antes o retorno null era tratado como sucesso e o toast "Mídias salvas com sucesso"
      // aparecia com a área de imagem vazia (sintoma do relatório de 26/07).
      // Upload do ícone se houver arquivo novo
      if (icone.file) {
        const retorno = await uploadIcone(idAlvo, icone.file, icone.nome);
        if (!retorno) {
          throw new Error("Falha no upload do ícone");
        }
        idAlvo = retorno.id;
        midiasAtualizadas.iconeUrl = retorno.url;
        midiasAtualizadas.iconeNomeArquivo = icone.nome;
      } else if (icone.url) {
        // Manter URL existente
        midiasAtualizadas.iconeUrl = icone.url;
        midiasAtualizadas.iconeNomeArquivo = icone.nome;
      }

      // Upload da figura se houver arquivo novo (uploadFigura re-lança em falha)
      if (figura.file) {
        const retorno = await uploadFigura(idAlvo, figura.file, figura.nome);
        if (!retorno) {
          throw new Error("Falha no upload da figura");
        }
        idAlvo = retorno.id;
        midiasAtualizadas.figuraUrl = retorno.url;
        midiasAtualizadas.figuraNomeArquivo = figura.nome;
      } else if (figura.url) {
        // Manter URL existente
        midiasAtualizadas.figuraUrl = figura.url;
        midiasAtualizadas.figuraNomeArquivo = figura.nome;
      }

      // Upload do desenho técnico se houver arquivo novo
      if (desenhoTecnico.file) {
        const retorno = await uploadDesenhoTecnico(idAlvo, desenhoTecnico.file, desenhoTecnico.nome);
        if (!retorno) {
          throw new Error("Falha no upload do desenho técnico");
        }
        idAlvo = retorno.id;
        midiasAtualizadas.desenhoTecnicoUrl = retorno.url;
        midiasAtualizadas.desenhoTecnicoNomeArquivo = desenhoTecnico.nome;
      } else if (desenhoTecnico.url) {
        // Manter URL existente
        midiasAtualizadas.desenhoTecnicoUrl = desenhoTecnico.url;
        midiasAtualizadas.desenhoTecnicoNomeArquivo = desenhoTecnico.nome;
      }

      // Reporte de 31/08 (item 6): aba em que o usuário só DIGITOU o nome (sem escolher
      // arquivo) grava apenas o nome — é como o produto assume uma mídia já existente no
      // acervo da empresa. Sem isto o nome não tinha caminho de gravação nenhum.
      const semArquivo: Array<["icone" | "figura" | "desenhoTecnico", string | undefined]> = [];
      if (!icone.file && icone.nome !== nomesIniciais.current.icone) semArquivo.push(["icone", icone.nome]);
      if (!figura.file && figura.nome !== nomesIniciais.current.figura) semArquivo.push(["figura", figura.nome]);
      if (!desenhoTecnico.file && desenhoTecnico.nome !== nomesIniciais.current.desenhoTecnico)
        semArquivo.push(["desenhoTecnico", desenhoTecnico.nome]);

      const semArquivoNoAcervo: string[] = [];
      for (const [tipo, nome] of semArquivo) {
        if (!nome?.trim()) continue;
        const resultado = await definirNomeMidiaPerfil(idAlvo, tipo, nome);
        if (!resultado) {
          throw new Error("Falha ao gravar o nome do arquivo");
        }
        idAlvo = resultado.id ?? idAlvo;
        if (!resultado.arquivoExiste) semArquivoNoAcervo.push(resultado.nomeArquivo);
        if (tipo === "icone") {
          midiasAtualizadas.iconeUrl = resultado.url;
          midiasAtualizadas.iconeNomeArquivo = resultado.nomeArquivo;
        } else if (tipo === "figura") {
          midiasAtualizadas.figuraUrl = resultado.url;
          midiasAtualizadas.figuraNomeArquivo = resultado.nomeArquivo;
        } else {
          midiasAtualizadas.desenhoTecnicoUrl = resultado.url;
          midiasAtualizadas.desenhoTecnicoNomeArquivo = resultado.nomeArquivo;
        }
      }

      onSave(midiasAtualizadas, idAlvo);
      if (semArquivoNoAcervo.length > 0) {
        openNotification({
          message:
            `Nome gravado, mas não há arquivo com este nome no acervo da empresa ` +
            `(${semArquivoNoAcervo.join(", ")}). A imagem só aparecerá quando o arquivo for enviado.`,
          type: "warning",
        });
      } else {
        openNotification({ message: "Mídias salvas com sucesso", type: "success" });
      }
    } catch (error: any) {
      // FASE 05 — 6ª validação/PO 2026-08-06: exibir a mensagem REAL do erro (ex.: "Apenas arquivos
      // .BMP são aceitos para ícone") em vez do texto fixo que sempre citava "Figura" — enganava
      // quem enviava um Ícone. O Ícone continua aceitando somente .BMP (decisão do PO).
      console.error("Erro ao fazer upload de mídias:", error);
      openNotification({
        message: error?.message || "Erro ao salvar as mídias — a imagem não foi gravada. Verifique o arquivo e tente novamente.",
        type: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxWidth: 700 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ color: "#4a4a4a" }}>
            Mídias (Perfil {perfil.codigo || "Novo"})
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          TabIndicatorProps={{ style: { display: "none" } }}
          sx={{
            minHeight: "auto",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              backgroundColor: "#7ab8cc",
              color: "#fff",
              marginRight: "2px",
              minHeight: 40,
              "&.Mui-selected": {
                backgroundColor: "#3C90AD",
                color: "#fff",
              },
            },
          }}
        >
          <Tab label="ÍCONE" />
          <Tab label="FIGURA" />
          {EXIBIR_ABA_DESENHO_TECNICO && <Tab label="DESENHO TÉCNICO" />}
        </Tabs>

        <TabPanel value={tabIndex} index={0}>
          <UploadArea
            accept=".bmp"
            arquivoAtual={icone.url}
            nomeArquivo={icone.nome}
            onFileSelect={(file) => handleFileSelect("icone", file)}
            onFileRejected={(motivo) => openNotification({ message: motivo, type: "warning" })}
          />
          <CampoNomeArquivo
            valor={icone.nome || ""}
            onChange={(v) => setIcone((a) => ({ ...a, nome: v }))}
            extensoes=".BMP"
            produtosCompartilhando={compartilhandoIcone}
          />
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          {/* FASE 05 — 4ª validação/D8 2026-07-30: alinhado ao backend (UploadFigura aceita
              .png/.jpg/.jpeg desde a 3ª validação); antes o modal só aceitava .png e o .jpg
              era rejeitado em silêncio. */}
          <UploadArea
            accept=".png,.jpg,.jpeg"
            arquivoAtual={figura.url}
            nomeArquivo={figura.nome}
            onFileSelect={(file) => handleFileSelect("figura", file)}
            onFileRejected={(motivo) => openNotification({ message: motivo, type: "warning" })}
          />
          <CampoNomeArquivo
            valor={figura.nome || ""}
            onChange={(v) => setFigura((a) => ({ ...a, nome: v }))}
            extensoes=".PNG, .JPG, .JPEG"
            produtosCompartilhando={compartilhandoFigura}
          />
        </TabPanel>

        {EXIBIR_ABA_DESENHO_TECNICO && (
          <TabPanel value={tabIndex} index={2}>
            <UploadArea
              accept=".png,.pdf,.jpg,.jpeg"
              arquivoAtual={desenhoTecnico.url}
              nomeArquivo={desenhoTecnico.nome}
              onFileSelect={(file) => handleFileSelect("desenhoTecnico", file)}
              onFileRejected={(motivo) => openNotification({ message: motivo, type: "warning" })}
            />
            <CampoNomeArquivo
              valor={desenhoTecnico.nome || ""}
              onChange={(v) => setDesenhoTecnico((a) => ({ ...a, nome: v }))}
              extensoes=".PNG, .PDF, .JPG, .JPEG"
              produtosCompartilhando={compartilhandoDesenho}
            />
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="text" disabled={uploading}>
          CANCELAR
        </Button>
        <Button
          onClick={handleSalvar}
          variant="contained"
          endIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          // Ajustes 25/08 (item 3): SALVAR só habilita depois de escolher algum arquivo —
          // antes ficava ativo na abertura, sem nada para gravar.
          disabled={uploading || (!temArquivoNovo && !temNomeAlterado)}
          sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
        >
          {uploading ? "SALVANDO..." : "SALVAR"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
